import type { PaymentSummary } from './payment';

/**
 * Named union — exhaustiveness enforced by Record<BookingStatus, …> in BookingStatusBadge.
 * Use a named type alias rather than an inline string union (Effective TypeScript Item 7 & 33).
 * NOTE (022): OpenAPI says POST /check-out transitions to COMPLETED.
 * CHECKED_OUT is preserved for legacy wire data; treat it as equivalent at render sites.
 *
 * Deferred-payment flow statuses (032):
 *   PENDING_OWNER_CONFIRMATION — booking is awaiting owner accept/decline.
 *   AWAITING_GUEST_PAYMENT     — owner accepted; guest must complete payment.
 *   DEPOSIT_PAID_AWAITING_BALANCE — deposit recorded; balance still outstanding.
 */
export type BookingStatus =
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'PENDING'
  | 'AWAITING_PAYMENT'
  | 'CHECKED_IN'
  | 'CHECKED_OUT'  // legacy wire value — may appear on older records
  | 'COMPLETED'
  // ── 032-booking-recovery-linking — deferred-flow wire statuses ──────────
  | 'PENDING_OWNER_CONFIRMATION'
  | 'AWAITING_GUEST_PAYMENT'
  | 'DEPOSIT_PAID_AWAITING_BALANCE';

/** Terminal statuses — edit and record actions must be disabled for these. */
export const TERMINAL_BOOKING_STATUSES: ReadonlySet<BookingStatus> = new Set([
  'COMPLETED',
  'CANCELLED',
  'CHECKED_OUT',
  // Deferred-flow statuses (032) are non-terminal — do not add them here.
]);

/** Known translated booking sources. Exhaustively handled in BookingSourceBadge. */
export type KnownBookingSource = 'DIRECT' | 'AIRBNB';

/**
 * Wire type for BookingResponse.source — unconstrained string with known values.
 * Use KnownBookingSource for exhaustive badge rendering; use BookingSource for
 * the wire field that may include future unknown values.
 * Legacy bookings with absent/null source must fall back to 'DIRECT' at the call site.
 */
export type BookingSource = KnownBookingSource | (string & NonNullable<unknown>);

export interface GuestInfo {
  guestId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  numberOfGuests?: number;
}

/**
 * Mirrors BookingResponse in open-api.json exactly — flat, no nesting added.
 * pricePerNight / totalPrice are string because the OpenAPI schema declares them
 * as string (preserving decimal precision on the wire).
 */
export interface Booking {
  bookingId: string;
  apartmentId: string;
  bookedAvailabilityId: string;
  checkInDate: string;   // "YYYY-MM-DD"
  checkOutDate: string;  // "YYYY-MM-DD"
  nights: number;
  guestInfo: GuestInfo;
  pricePerNight: string;
  totalPrice: string;
  status: BookingStatus;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
  checkedInAt?: string;
  checkedOutAt?: string;
  cancelledAt?: string;
  paymentId?: string;
  paymentExpiresAt?: string;

  /**
   * Present only on bookings created after 004-payment-gateway-integration is deployed.
   * Absent (undefined) for legacy bookings — UI must render gracefully when undefined.
   */
  readonly payment?: PaymentSummary;

  /**
   * Booking source channel. Absent on legacy bookings — fall back to 'DIRECT' at every call site.
   * Wire values: 'DIRECT' | 'AIRBNB' (open-api.json BookingResponse, confirmed 015-airbnb-booking-source).
   */
  readonly source?: BookingSource;

  // ── 022-checkin-checkout-times ──────────────────────────────────────────
  /**
   * Scheduled check-in time (UTC). "HH:mm" string snapshotted from first availability.
   * Absent on bookings created before this feature — render as date-only.
   */
  readonly scheduledCheckInTime?: string;
  /**
   * Scheduled check-out time (UTC). "HH:mm" string snapshotted from last availability.
   * Absent on bookings created before this feature — render as date-only.
   */
  readonly scheduledCheckOutTime?: string;
  /**
   * The originally booked check-out time ("HH:mm") before any late-checkout deviation.
   * Present when the check-out time has been moved later.
   */
  readonly originalCheckOutTime?: string;
  /** Whether auto check-in was enabled at the time the booking was created. */
  readonly autoCheckIn?: boolean;
  /** Whether auto check-out was enabled at the time the booking was created. */
  readonly autoCheckOut?: boolean;
  // ── end 022-checkin-checkout-times ────────────────────────────────────

  // ── 032-booking-recovery-linking — deferred-payment wire fields ──────────
  /** ISO 8601 deadline by which the owner must accept or decline this booking. */
  readonly ownerReviewExpiresAt?: string;
  /** ISO 8601 deadline by which the guest must complete payment after owner acceptance. */
  readonly guestActionExpiresAt?: string;
  /** URL path for the guest's secure booking payment page. */
  readonly secureBookingPath?: string;
  /** ISO 8601 expiry of the guest's secure booking access link. */
  readonly secureBookingExpiresAt?: string;
  /** Deposit amount (string, decimal-safe). Present on deposit-flow bookings. */
  readonly depositAmount?: string;
  /** Remaining balance due after deposit payment (string, decimal-safe). */
  readonly remainingBalanceAmount?: string;
  /** ISO 8601 deadline by which the guest must pay the remaining balance. */
  readonly balanceDueAt?: string;
  /** Raw payment state string from the wire (e.g. 'INITIATED', 'DEPOSIT_PAID'). */
  readonly paymentState?: string;
  // ── end deferred-payment wire fields ──────────────────────────────────

  // ── 032-support-case-management ───────────────────────────────────────────
  /**
   * ID of the active support case for this booking, if one exists.
   * Derived by the frontend from the support-cases list query; NOT a wire field.
   * Set to the caseId of any case in OPEN or IN_PROGRESS status for this bookingId.
   */
  readonly activeSupportCaseId?: string;

  /**
   * Optimistic-lock version counter. Incremented on every booking state mutation.
   * Required as expectedBookingVersion when submitting RECORD_OFFLINE_DEPOSIT or
   * RECORD_OFFLINE_FULL_PAYMENT support actions.
   * Backend will add this field to BookingResponse (B3 contract).
   */
  readonly version?: number;

  /**
   * Secure booking payment token lifecycle state.
   * ACTIVE | EXPIRED | SETTLED | INVALIDATED
   * Used to determine action eligibility:
   *   RESEND_PAYMENT_ACCESS requires ACTIVE
   *   RESET_EXPIRED_ACCESS requires EXPIRED
   * Backend will add this field to BookingResponse (B3 contract).
   */
  readonly secureBookingTokenState?: 'ACTIVE' | 'EXPIRED' | 'SETTLED' | 'INVALIDATED';
  // ── end 032-support-case-management ───────────────────────────────────────
}

/** Mirrors CancelBookingRequest in open-api.json. */
export interface CancelBookingRequest {
  cancellationReason: string;
}

// ── 022-checkin-checkout-times ───────────────────────────────────────────────

/** Request body for PATCH /bookings/{id}/check-in-time */
export interface AdjustCheckInTimeRequest {
  newTime: string;
}

/** Request body for PATCH /bookings/{id}/check-out-time */
export interface AdjustCheckOutTimeRequest {
  newTime: string;
}

/**
 * Discriminated result of adjustCheckOutTime.
 * Backend returns HTTP 200 when the change is applied immediately,
 * or HTTP 202 when it exceeds the self-service limit and a TimeAdjustmentInquiry is created.
 */
export type AdjustCheckOutTimeResult =
  | { type: 'applied'; booking: Booking }
  | { type: 'pending'; booking: Booking };

// ── end 022-checkin-checkout-times ───────────────────────────────────────────
